import { useState, useCallback, useMemo } from 'react';
import { MutationDefinition, MutationOverrides } from '../mutations/types';
import { executeMutation } from '../mutations/executeMutation';
import { usePulseClient } from './shared';

export type MutationStatus<TResult, TError extends Error = Error> = {
  loading: boolean;
  error: TError | null;
  data: TResult | undefined;
  reset: () => void;
};

type MutationState<TResult, TError extends Error = Error> = {
  loading: boolean;
  error: TError | null;
  data: TResult | undefined;
};

const initialState = { loading: false, error: null, data: undefined };

export function useMutation<
  TParams = void,
  TResult = void,
  TError extends Error = Error,
>(
  definition: MutationDefinition<TParams, TResult, TError>
): [
  (
    params: TParams,
    overrides?: MutationOverrides<TParams, TResult, TError>
  ) => Promise<TResult>,
  MutationStatus<TResult, TError>,
] {
  const client = usePulseClient();
  const [state, setMutationState] =
    useState<MutationState<TResult, TError>>(initialState);

  const mutate = useCallback(
    async (
      params: TParams,
      overrides?: MutationOverrides<TParams, TResult, TError>
    ) => {
      if (!client) {
        return Promise.reject(
          new Error(
            'Pulse client was not found in context — did you forget to provide it?'
          )
        );
      }

      setMutationState({ loading: true, error: null, data: undefined });

      try {
        const result = await executeMutation(
          definition,
          params,
          client,
          overrides
        );

        setMutationState({ loading: false, error: null, data: result });

        return result;
      } catch (error: any) {
        setMutationState({ loading: false, error, data: undefined });

        throw error;
      }
    },
    [client, definition]
  );

  const reset = useCallback(() => {
    setMutationState(initialState);
  }, []);

  return useMemo(() => [mutate, { ...state, reset }], [mutate, state, reset]);
}
